import { PalmettoIcon } from '@palmetto/dsm-components';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';

import type { RichTextNode } from 'types';

interface ChecklistTooltipPopoverProps {
  content: RichTextNode[];
  onClose?: () => void;
}

const serializeNode = (node: RichTextNode): string => {
  if (!node.children) {
    let text = (node.text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (node.bold) text = `<strong>${text}</strong>`;
    if (node.italic) text = `<em>${text}</em>`;
    return text;
  }

  const children = node.children.map(serializeNode).join('');

  switch (node.type) {
    case 'bulleted-list':
      return `<ul class="list-disc pl-[1.25em] mb-[0.375em] last:mb-0">${children}</ul>`;
    case 'numbered-list':
      return `<ol class="list-decimal pl-[1.25em] mb-[0.375em] last:mb-0">${children}</ol>`;
    case 'list-item':
      return `<li>${children}</li>`;
    case 'link':
      return `<a href="${node.url ?? ''}" class="text-text-action underline">${children}</a>`;
    default:
      return `<p class="mb-[0.375em] last:mb-0">${children}</p>`;
  }
};

const setLinkTargetAttrs = (node: Element) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
};

const serializeDocument = (nodes: RichTextNode[]) => {
  DOMPurify.addHook('afterSanitizeAttributes', setLinkTargetAttrs);
  try {
    return DOMPurify.sanitize(nodes.map(serializeNode).join(''), {
      ALLOWED_URI_REGEXP: /^https?:/i,
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', setLinkTargetAttrs);
  }
};

export const ChecklistTooltipPopover = ({ content, onClose }: ChecklistTooltipPopoverProps) => {
  const htmlContent = useMemo(() => ({ __html: serializeDocument(content) }), [content]);

  return (
    <div
      role="dialog"
      aria-label="Requirement guidance"
      className="w-[300px] max-w-full rounded-8 border border-border-default bg-canvas-default p-12 shadow-lg"
    >
      <div className="mb-8 flex items-start justify-end">
        <button
          type="button"
          aria-label="Close guidance"
          onClick={onClose}
          className="shrink-0 text-text-tertiary hover:text-text-primary"
        >
          <PalmettoIcon name="x" size="xs" />
        </button>
      </div>
      <div
        className="rte-content wrap-break-words text-sm text-text-primary"
        dangerouslySetInnerHTML={htmlContent}
      />
    </div>
  );
};
